/**
 * Per-user snooze for the "encourage 2FA" dashboard nudge (RD-SEC-008).
 *
 * Dismissing the banner ("Not now") goes quiet for 14 days, then it returns — a
 * gentle, persistent security reminder rather than a per-session nag or a
 * permanent silence. Keyed by user id in localStorage so one admin's dismissal
 * never suppresses another's on a shared device (the bug the old global,
 * session-scoped flag had); it stops for good once the user actually enrolls.
 *
 * `now` is injectable for tests. Date.now() is correct here — this is a relative
 * device-local timer, NOT a tenant-tz calendar date, so the tenantTodayDate rule
 * does not apply.
 *
 * RD-NOTICES-V2: write-throughs to the server as notice_key `mfa_nudge`
 * (`snoozed_until`). The local value is a raw epoch-ms string (not an ISO
 * instant), so the sync/merge helpers convert at the boundary only.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso } from '../notices/noticeReceiptMath';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';

const PREFIX = 'crewvex.mfaNudge.snooze.';
export const MFA_NUDGE_SNOOZE_DAYS = 14;
const SNOOZE_MS = MFA_NUDGE_SNOOZE_DAYS * 24 * 60 * 60 * 1000;
export const MFA_NUDGE_NOTICE_KEY = 'mfa_nudge';

function readUntilMs(userId) {
  try {
    const until = Number(localStorage.getItem(PREFIX + userId));
    return Number.isFinite(until) ? until : NaN;
  } catch (_) {
    return NaN;
  }
}

function writeUntilMs(userId, untilMs) {
  try {
    localStorage.setItem(PREFIX + userId, String(untilMs));
    return true;
  } catch (_) {
    return false;
  }
}

/** True when the user dismissed the nudge within the last MFA_NUDGE_SNOOZE_DAYS. */
export function isMfaNudgeSnoozed(userId, now = Date.now()) {
  if (!userId) return false;
  const until = readUntilMs(userId);
  return Number.isFinite(until) && until > now;
}

/** Snooze the nudge for this user for MFA_NUDGE_SNOOZE_DAYS from `now`. */
export function snoozeMfaNudge(userId, now = Date.now(), opts = {}) {
  if (!userId) return;
  // MONOTONIC (class B): never shorten a snooze another device set further out.
  const current = readUntilMs(userId);
  const until = Math.max(now + SNOOZE_MS, Number.isFinite(current) ? current : -Infinity);
  writeUntilMs(userId, until);
  if (opts.skipSync) return;
  const tenantId = tenantIdForNoticeWrite(userId);
  if (!tenantId) return;
  upsertNoticeReceipts({
    tenantId,
    userId,
    rows: [{ notice_key: MFA_NUDGE_NOTICE_KEY, snoozed_until: new Date(until).toISOString() }],
  });
}

/** Server row → local store, MONOTONIC (never move the snooze back). */
export function mergeMfaNudgeSnoozeFromServer(userId, snoozedUntilIso) {
  if (!userId || !snoozedUntilIso) return;
  const serverMs = Date.parse(snoozedUntilIso);
  if (Number.isNaN(serverMs)) return;
  const currentMs = readUntilMs(userId);
  if (!Number.isFinite(currentMs) || isNewerIso(snoozedUntilIso, new Date(currentMs).toISOString())) {
    writeUntilMs(userId, serverMs);
  }
}

/** Snapshot for the one-time post-hydration upload. */
export function collectMfaNudgeReceiptsForUpload(userId) {
  if (!userId) return [];
  const untilMs = readUntilMs(userId);
  if (!Number.isFinite(untilMs)) return [];
  return [{ notice_key: MFA_NUDGE_NOTICE_KEY, snoozed_until: new Date(untilMs).toISOString() }];
}

export function ownsMfaNudgeNoticeKey(noticeKey) {
  return noticeKey === MFA_NUDGE_NOTICE_KEY;
}
